import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowLeft, Building2, Network, FileText, Plus, Trash2, CheckCircle, Upload, Truck, Package, Download, Pencil, Server, MessageSquare, Send, ShoppingCart, RefreshCw, IndianRupee, CalendarClock, ShieldOff } from 'lucide-react';
import { toast } from 'sonner';
import { tierLabel } from '@/utils/tierLabels';
import TechnicalImplementation from './TechnicalImplementation';

const PROVIDERS = ['Airtel', 'Vodafone', 'GTPL', 'Direct', 'BSNL', 'Anonet'];
const NOTE_TYPES = [
  { value: 'general', label: 'General Note', color: 'bg-slate-100 text-slate-700' },
  { value: 'negotiation', label: 'Negotiation', color: 'bg-blue-100 text-blue-700' },
  { value: 'requirement', label: 'Requirement', color: 'bg-purple-100 text-purple-700' },
  { value: 'followup', label: 'Follow-up', color: 'bg-amber-100 text-amber-700' },
  { value: 'meeting', label: 'Meeting Notes', color: 'bg-emerald-100 text-emerald-700' }
];

// ============== Tier Change (Master / Sub / LCO) — Admin only ==============
function TierChangeButton({ customer, API, onDone }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  // Default target: promote LCO→Sub, demote Sub→LCO, and a Master defaults to
  // stepping down one rung to Sub Distributor.
  const [newType, setNewType] = useState(() => {
    if (customer.customer_type === 'lco') return 'iptv_distributor';
    if (customer.customer_type === 'iptv_distributor') return 'partner';
    return 'iptv_distributor';
  });
  const [newParentPartnerId, setNewParentPartnerId] = useState(customer.parent_partner_id || '');
  const [newParentDistributorId, setNewParentDistributorId] = useState('');
  const [migratePricing, setMigratePricing] = useState(true);
  const [partners, setPartners] = useState([]);
  const [distributors, setDistributors] = useState([]);

  const isPromote = (
    (customer.customer_type === 'lco' && newType === 'iptv_distributor') ||
    (customer.customer_type === 'lco' && newType === 'partner') ||
    (customer.customer_type === 'iptv_distributor' && newType === 'partner')
  );
  const isDemote = (
    (customer.customer_type === 'iptv_distributor' && newType === 'lco') ||
    (customer.customer_type === 'partner' && newType === 'iptv_distributor') ||
    (customer.customer_type === 'partner' && newType === 'lco')
  );

  useEffect(() => {
    if (!open || newType === 'partner') return;
    axios.get(`${API}/customers?customer_type=partner`)
      .then(r => {
        const d = r.data;
        const list = Array.isArray(d) ? d : (d.customers || d.items || []);
        setPartners(list.filter(c => ['partner', 'bulk_bandwidth'].includes(c.customer_type)));
      })
      .catch(err => {
        console.error('[TierChange] Failed to load partners:', err);
        toast.error('Could not load Master Distributors list');
      });
  }, [open, newType, API]);

  useEffect(() => {
    if (!open || !newParentPartnerId || newType !== 'lco') { setDistributors([]); return; }
    axios.get(`${API}/customers?parent_partner_id=${newParentPartnerId}`)
      .then(r => {
        const d = r.data;
        const list = Array.isArray(d) ? d : (d.customers || d.items || []);
        setDistributors(list.filter(c => c.customer_type === 'iptv_distributor'));
      })
      .catch(err => console.error('[TierChange] Failed to load distributors:', err));
  }, [open, newParentPartnerId, newType, API]);

  const submit = async () => {
    if (newType !== 'partner' && !newParentPartnerId) {
      toast.error('Select a parent Master Distributor');
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/customers/${customer.customer_id}/change-tier`, {
        new_type: newType,
        new_parent_partner_id: newType === 'partner' ? null : newParentPartnerId,
        new_parent_distributor_id: newType === 'lco' ? (newParentDistributorId || null) : null,
        migrate_pricing: migratePricing,
      });
      toast.success(`${r.data.message}. ${r.data.pricing_rows_migrated} pricing row(s) migrated.`);
      setOpen(false);
      onDone && onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Tier change failed');
    }
    setLoading(false);
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="flex-1 sm:flex-none text-sm border-amber-300 text-amber-700 hover:bg-amber-50"
        data-testid="change-tier-btn"
      >
        <RefreshCw className="h-3.5 w-3.5 mr-1" /> Change Tier
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Change Customer Tier</DialogTitle>
            <DialogDescription>
              Move <b>{customer.company_name || customer.name}</b> between
              Master Distributor, Sub Distributor and LCO. Wallet balance,
              subscriptions and customer history are preserved (same customer_id).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Current tier</Label>
              <div className="text-sm mt-1">
                <Badge variant="outline">
                  {customer.customer_type === 'partner' ? 'Master Distributor' :
                   customer.customer_type === 'iptv_distributor' ? 'Sub Distributor' :
                   customer.customer_type === 'lco' ? 'LCO' :
                   customer.customer_type?.replace('_', ' ')}
                </Badge>
              </div>
            </div>
            <div>
              <Label>Change to</Label>
              <Select value={newType} onValueChange={setNewType}>
                <SelectTrigger data-testid="change-tier-new-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {customer.customer_type !== 'partner' && (
                    <SelectItem value="partner" data-testid="tier-option-master">Master Distributor</SelectItem>
                  )}
                  {customer.customer_type !== 'iptv_distributor' && (
                    <SelectItem value="iptv_distributor" data-testid="tier-option-sub">Sub Distributor</SelectItem>
                  )}
                  {customer.customer_type !== 'lco' && (
                    <SelectItem value="lco" data-testid="tier-option-lco">LCO</SelectItem>
                  )}
                  {/* Allow lco→lco re-parenting */}
                  {customer.customer_type === 'lco' && (
                    <SelectItem value="lco" data-testid="tier-option-lco-reparent">LCO (re-parent)</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            {newType !== 'partner' && (
              <div>
                <Label>Parent Master Distributor *</Label>
                <Select value={newParentPartnerId} onValueChange={setNewParentPartnerId}>
                  <SelectTrigger data-testid="change-tier-partner"><SelectValue placeholder="Select Master Distributor…" /></SelectTrigger>
                  <SelectContent>
                    {partners.map(p => (
                      <SelectItem key={p.customer_id} value={p.customer_id}>
                        {p.company_name || p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {newType === 'lco' && (
              <div>
                <Label>Parent Distributor <span className="text-xs text-slate-500">(optional — leave blank for legacy 2-tier)</span></Label>
                <Select
                  value={newParentDistributorId || 'none'}
                  onValueChange={(v) => setNewParentDistributorId(v === 'none' ? '' : v)}
                >
                  <SelectTrigger data-testid="change-tier-distributor"><SelectValue placeholder="Direct under Partner" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Direct under Partner —</SelectItem>
                    {distributors.map(d => (
                      <SelectItem key={d.customer_id} value={d.customer_id}>
                        {d.company_name || d.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex items-start gap-2 rounded-md border p-3 bg-slate-50">
              <Checkbox
                id="migrate-pricing"
                checked={migratePricing}
                onCheckedChange={(v) => setMigratePricing(!!v)}
                data-testid="change-tier-migrate-pricing"
              />
              <div>
                <Label htmlFor="migrate-pricing" className="cursor-pointer text-sm">
                  Migrate existing pricing overrides
                </Label>
                <p className="text-xs text-slate-500">
                  {isPromote
                    ? 'Move iptv_lco_pricing rows into iptv_distributor_pricing so the chain keeps working.'
                    : isDemote
                    ? 'Move iptv_distributor_pricing rows into iptv_lco_pricing.'
                    : 'Preserve LCO pricing rows.'}
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              onClick={submit}
              disabled={loading || (newType !== 'partner' && !newParentPartnerId)}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="change-tier-submit"
            >
              {loading
                ? 'Converting…'
                : `Convert to ${
                    newType === 'partner' ? 'Master Distributor'
                    : newType === 'iptv_distributor' ? 'Sub Distributor'
                    : 'LCO'
                  }`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}



function AuditVisibilityToggle({ customer, API }) {
  const [enabled, setEnabled] = useState(!!customer.audit_visibility_enabled);
  const [saving, setSaving] = useState(false);
  const toggle = async () => {
    setSaving(true);
    try {
      const r = await axios.post(
        `${API}/admin/customers/${customer.customer_id}/audit-visibility`,
        { enabled: !enabled }
      );
      setEnabled(r.data.audit_visibility_enabled);
      toast.success(`Audit visibility ${r.data.audit_visibility_enabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Toggle failed');
    }
    setSaving(false);
  };
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={toggle}
      disabled={saving}
      className={`flex-1 sm:flex-none text-sm ${enabled ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}
      data-testid="audit-visibility-toggle-btn"
    >
      {enabled ? <CheckCircle className="h-3.5 w-3.5 mr-1" /> : <ShieldOff className="h-3.5 w-3.5 mr-1" />}
      Audit View: {enabled ? 'ON' : 'OFF'}
    </Button>
  );
}



// ============== IPTV External API Access (per-customer) ==============
function IPTVApiAccessCard({ customer, API, canEdit }) {
  const isPartnerType = ['partner', 'bulk_bandwidth'].includes(customer.customer_type);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ips, setIps] = useState('');
  const [rate, setRate] = useState(60);
  const [secret, setSecret] = useState(null);
  const [showDocs, setShowDocs] = useState(false);

  const load = React.useCallback(async () => {
    try {
      const r = await axios.get(`${API}/iptv-api/customers/${customer.customer_id}/credentials`);
      setStatus(r.data);
      setIps((r.data.allowed_ips || []).join(', '));
      setRate(r.data.rate_limit_per_min || 60);
    } catch { /* */ }
  }, [API, customer.customer_id]);

  useEffect(() => { if (isPartnerType) load(); }, [isPartnerType, load]);

  if (!isPartnerType) return null;

  const generate = async () => {
    if (status?.has_api_key && !window.confirm('Rotate the API key & secret? The old credentials will stop working immediately.')) return;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/iptv-api/customers/${customer.customer_id}/credentials/generate`, {});
      setSecret(r.data);
      toast.success(r.data.rotated ? 'Credentials rotated' : 'Credentials generated');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate');
    } finally { setLoading(false); }
  };

  const saveConfig = async () => {
    setLoading(true);
    try {
      const allowed_ips = ips.split(',').map(s => s.trim()).filter(Boolean);
      await axios.put(`${API}/iptv-api/customers/${customer.customer_id}/credentials`, {
        allowed_ips, is_active: status?.is_active ?? true, rate_limit_per_min: Number(rate) || 60,
      });
      toast.success('IPTV API settings saved');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setLoading(false); }
  };

  const toggleActive = async () => {
    setLoading(true);
    try {
      if (status?.is_active) {
        await axios.delete(`${API}/iptv-api/customers/${customer.customer_id}/credentials`);
        toast.success('IPTV API access deactivated');
      } else {
        const allowed_ips = ips.split(',').map(s => s.trim()).filter(Boolean);
        await axios.put(`${API}/iptv-api/customers/${customer.customer_id}/credentials`, {
          allowed_ips, is_active: true, rate_limit_per_min: Number(rate) || 60,
        });
        toast.success('IPTV API access activated');
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setLoading(false); }
  };

  const copy = (v) => { navigator.clipboard.writeText(v); toast.success('Copied'); };
  const base = `${API}/external/iptv`;

  const downloadDoc = async (slug, filename) => {
    try {
      const r = await axios.get(`${API}/iptv-api/docs/${slug}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download document');
    }
  };

  return (
    <Card className="lg:col-span-2" data-testid="iptv-api-card">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="flex items-center gap-2 text-base lg:text-lg">
          <Server className="h-5 w-5" /> IPTV Activation API
          {status?.has_api_key && (
            <Badge className={status.is_active ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-500'}>
              {status.is_active ? 'Active' : 'Inactive'}
            </Badge>
          )}
        </CardTitle>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => downloadDoc('api-doc', 'IPTV_API_Integration_Guide.md')} data-testid="iptv-api-download-doc">
            <Download className="h-4 w-4 mr-1" /> API Doc
          </Button>
          <Button variant="ghost" size="sm" onClick={() => downloadDoc('postman', 'IPTV_API.postman_collection.json')} data-testid="iptv-api-download-postman">
            Postman
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setShowDocs(s => !s)} data-testid="iptv-api-docs-toggle">
            {showDocs ? 'Hide Docs' : 'Docs'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {!status?.iptv_enabled ? (
          <p className="text-amber-600 text-sm">This customer has no IPTV (ADIGITAL) access token configured. Configure IPTV access first to enable the API.</p>
        ) : !status?.has_api_key ? (
          <div className="space-y-2">
            <p className="text-slate-600">No API credentials yet. Generate a key + secret so this partner can activate IPTV programmatically. New subscriptions made via the API appear in their portal automatically.</p>
            {canEdit && (
              <Button onClick={generate} disabled={loading} data-testid="iptv-api-generate-btn">
                Generate API Credentials
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-500">API Key</Label>
                <div className="flex items-center gap-2">
                  <code className="text-xs bg-slate-100 px-2 py-1 rounded font-mono break-all flex-1" data-testid="iptv-api-key-value">{status.api_key}</code>
                  <Button variant="outline" size="sm" onClick={() => copy(status.api_key)}>Copy</Button>
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Rate Limit (req/min)</Label>
                <Input type="number" value={rate} onChange={e => setRate(e.target.value)} disabled={!canEdit} data-testid="iptv-api-rate-input" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs text-slate-500">Allowed IPs (comma-separated)</Label>
                <Input value={ips} onChange={e => setIps(e.target.value)} placeholder="e.g. 103.21.x.x, 49.36.x.x" disabled={!canEdit} data-testid="iptv-api-ips-input" />
                <p className="text-[11px] text-slate-400 mt-1">Only requests from these IPs are accepted. Leave empty to block all (must set at least one to use the API).</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
              {status.last_hit_at && <span>Last call: {new Date(status.last_hit_at).toLocaleString('en-IN')} from {status.last_hit_ip}</span>}
            </div>
            {canEdit && (
              <div className="flex flex-wrap gap-2">
                <Button onClick={saveConfig} disabled={loading} data-testid="iptv-api-save-btn">Save Settings</Button>
                <Button variant="outline" onClick={generate} disabled={loading} data-testid="iptv-api-rotate-btn">Rotate Key</Button>
                <Button variant="outline" onClick={toggleActive} disabled={loading}
                        className={status.is_active ? 'text-rose-600 border-rose-200 hover:bg-rose-50' : 'text-emerald-600 border-emerald-200'}
                        data-testid="iptv-api-toggle-btn">
                  {status.is_active ? 'Deactivate' : 'Activate'}
                </Button>
              </div>
            )}
          </>
        )}

        {showDocs && (
          <div className="border rounded-lg p-3 bg-slate-50 space-y-2 text-xs" data-testid="iptv-api-docs">
            <p className="font-semibold text-slate-700">Authentication (per request)</p>
            <p className="text-slate-600">Send headers <code>X-API-Key</code>, <code>X-Timestamp</code> (UNIX seconds), and <code>X-Signature</code> where:</p>
            <pre className="bg-white border rounded p-2 overflow-x-auto">{`signature = HMAC_SHA256(
  api_secret,
  METHOD + "\\n" + PATH + "\\n" + TIMESTAMP + "\\n" + SHA256_hex(body)
)`}</pre>
            <p className="font-semibold text-slate-700 mt-2">Endpoints</p>
            <ul className="space-y-1 text-slate-600 font-mono">
              <li><span className="text-emerald-700">POST</span> {base}/activate</li>
              <li><span className="text-emerald-700">POST</span> {base}/renew</li>
              <li><span className="text-sky-700">GET</span> {base}/packages</li>
              <li><span className="text-sky-700">GET</span> {base}/subscription/{'{mobile_no}'}</li>
              <li><span className="text-sky-700">GET</span> {base}/transactions</li>
            </ul>
            <p className="font-semibold text-slate-700 mt-2">Sample activate body</p>
            <pre className="bg-white border rounded p-2 overflow-x-auto">{`{
  "package_id": "<package_id>",
  "mobile_no": "9999999999",
  "subscriber_name": "John Doe",
  "state": "Karnataka",
  "city": "Bangalore",
  "address": "...",
  "pincode": "560001"
}`}</pre>
            <p className="text-slate-500">Get valid package_ids from <code>GET /packages</code>. Subscriptions, wallet debits & commissions reflect in this partner's portal.</p>
          </div>
        )}
      </CardContent>

      <Dialog open={!!secret} onOpenChange={() => setSecret(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>API Credentials {secret?.rotated ? 'Rotated' : 'Generated'}</DialogTitle>
            <DialogDescription>Copy the secret now — it will not be shown again.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-500">API Key</Label>
              <div className="flex items-center gap-2">
                <code className="text-xs bg-slate-100 px-2 py-1 rounded font-mono break-all flex-1">{secret?.api_key}</code>
                <Button variant="outline" size="sm" onClick={() => copy(secret.api_key)}>Copy</Button>
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">API Secret</Label>
              <div className="flex items-center gap-2">
                <code className="text-xs bg-amber-50 border border-amber-200 px-2 py-1 rounded font-mono break-all flex-1" data-testid="iptv-api-secret-value">{secret?.api_secret}</code>
                <Button variant="outline" size="sm" onClick={() => copy(secret.api_secret)}>Copy</Button>
              </div>
            </div>
            <p className="text-xs text-rose-600">⚠ Remember to set the partner's Allowed IPs, otherwise all calls will be rejected.</p>
          </div>
          <DialogFooter>
            <Button onClick={() => setSecret(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


export default function CustomerDetail() {
  const { API, user } = useAuth();
  const navigate = useNavigate();
  const { customer_id } = useParams();
  const [customer, setCustomer] = useState(null);
  const [packages, setPackages] = useState([]);
  const [circuits, setCircuits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [discussions, setDiscussions] = useState([]);
  
  // Dialogs
  const [packageDialog, setPackageDialog] = useState(false);
  const [circuitDialog, setCircuitDialog] = useState(false);
  const [deliveryDialog, setDeliveryDialog] = useState({ open: false, circuit: null });
  const [docDialog, setDocDialog] = useState(false);
  const [poDialog, setPoDialog] = useState(false);
  const [editPackageId, setEditPackageId] = useState(null);
  const [noteDialog, setNoteDialog] = useState(false);

  // Payment follow-up settings dialog
  const [followupDialog, setFollowupDialog] = useState(false);
  const [followupForm, setFollowupForm] = useState({
    recurring_amount: '', recurring_cycle_days: 30, recurring_next_due_date: '',
    non_recurring_amount: '', non_recurring_due_date: '',
    payment_followup_assignee_email: '',
  });
  const [followupSaving, setFollowupSaving] = useState(false);
  const [staffOptions, setStaffOptions] = useState([]);
  
  const fileInputRef = useRef(null);

  // Discussion form
  const [noteForm, setNoteForm] = useState({
    note_type: 'general',
    title: '',
    content: ''
  });

  // Forms
  const [packageForm, setPackageForm] = useState({
    package_name: '', service_type: 'broadband', speed: '', 
    includes_tv: false, includes_ott: false, ott_platforms: '', 
    tv_channels: '', mrp: '', minimum_guarantee: '',
    operator_share_percent: '', isp_share_percent: ''
  });

  const [circuitForm, setCircuitForm] = useState({
    location_name: '', installation_address: '', city: '', state: '', pincode: '',
    bandwidth: '', last_mile_type: 'Fiber', ip_allocation: 'Dynamic', monthly_charges: '',
    last_mile_contact_name: '', last_mile_contact_phone: '', latitude: '', longitude: ''
  });

  const [deliveryForm, setDeliveryForm] = useState({
    provider: '', provider_circuit_id: '', ra_number: '',
    end_a_port_details: '', end_b_port_details: '', vlan_id: '',
    last_mile_contact_name: '', last_mile_contact_phone: '',
    delivery_notes: '', accounts_notes: '', delivery_date: new Date().toISOString().split('T')[0],
    latency_ms: '', speedtest_image: '', speedtest_image_filename: '', speedtest_image_mime: ''
  });

  const [docForm, setDocForm] = useState({ document_type: 'pan_card', document_url: '' });
  const [poProvider, setPoProvider] = useState('');
  const [editCustomerDialog, setEditCustomerDialog] = useState(false);
  const [customerEditForm, setCustomerEditForm] = useState({});
  const [editCircuitId, setEditCircuitId] = useState(null);

  // Helper function to check if user has any of the allowed roles
  const hasRole = (allowedRoles) => {
    const userRoles = user?.roles || (user?.role ? [user.role] : []);
    return allowedRoles.some(role => userRoles.includes(role));
  };

  // Role checks - now support multiple roles
  const canEdit = hasRole(['admin', 'onboarding', 'sales', 'enterprise_sales', 'lco_sales']);
  const canApprove = hasRole(['admin']);
  const canRaisePO = hasRole(['admin', 'accounts']);
  const canDeliver = hasRole(['admin', 'delivery']);
  const isSupport = hasRole(['support']) && !hasRole(['admin', 'onboarding', 'sales', 'accounts', 'delivery', 'noc']);

  const fetchData = async () => {
    try {
      const [customerRes, packagesRes, circuitsRes, discussionsRes] = await Promise.all([
        axios.get(`${API}/customers/${customer_id}`),
        axios.get(`${API}/packages?customer_id=${customer_id}`),
        axios.get(`${API}/circuits?customer_id=${customer_id}`),
        axios.get(`${API}/customers/${customer_id}/discussions`)
      ]);
      setCustomer(customerRes.data);
      setPackages(packagesRes.data);
      setCircuits(circuitsRes.data);
      setDiscussions(discussionsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
      navigate('/customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [API, customer_id]);

  // File upload
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setDocForm({ ...docForm, document_url: response.data.url });
      toast.success('File uploaded');
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // Package actions
  const handleAddPackage = async () => {
    try {
      const packageData = {
        ...packageForm,
        mrp: parseFloat(packageForm.mrp) || 0,
        minimum_guarantee: packageForm.minimum_guarantee ? parseFloat(packageForm.minimum_guarantee) : null,
        operator_share_percent: packageForm.operator_share_percent ? parseFloat(packageForm.operator_share_percent) : null,
        isp_share_percent: packageForm.isp_share_percent ? parseFloat(packageForm.isp_share_percent) : null
      };
      
      if (editPackageId) {
        // Update existing package
        await axios.put(`${API}/packages/${editPackageId}`, packageData);
        toast.success('Package updated');
      } else {
        // Create new package
        await axios.post(`${API}/packages`, { ...packageData, customer_id });
        toast.success('Package added');
      }
      setPackageDialog(false);
      setEditPackageId(null);
      setPackageForm({ package_name: '', service_type: 'broadband', speed: '', includes_tv: false, includes_ott: false, ott_platforms: '', tv_channels: '', mrp: '', minimum_guarantee: '', operator_share_percent: '', isp_share_percent: '' });
      fetchData();
    } catch (error) {
      toast.error(editPackageId ? 'Failed to update package' : 'Failed to add package');
    }
  };

  const handleEditPackage = (pkg) => {
    setEditPackageId(pkg.package_id);
    setPackageForm({
      package_name: pkg.package_name || '',
      service_type: pkg.service_type || 'broadband',
      speed: pkg.speed || '',
      includes_tv: pkg.includes_tv || false,
      includes_ott: pkg.includes_ott || false,
      ott_platforms: pkg.ott_platforms || '',
      tv_channels: pkg.tv_channels || '',
      mrp: pkg.mrp?.toString() || '',
      minimum_guarantee: pkg.minimum_guarantee?.toString() || '',
      operator_share_percent: pkg.operator_share_percent?.toString() || '',
      isp_share_percent: pkg.isp_share_percent?.toString() || ''
    });
    setPackageDialog(true);
  };

  // Circuit actions
  const handleAddCircuit = async () => {
    try {
      const circuitData = {
        ...circuitForm,
        monthly_charges: parseFloat(circuitForm.monthly_charges) || 0,
        latitude: circuitForm.latitude ? parseFloat(circuitForm.latitude) : null,
        longitude: circuitForm.longitude ? parseFloat(circuitForm.longitude) : null
      };
      
      if (editCircuitId) {
        // Update existing circuit
        await axios.put(`${API}/circuits/${editCircuitId}`, circuitData);
        toast.success('Circuit updated');
      } else {
        // Create new circuit
        await axios.post(`${API}/circuits`, { ...circuitData, customer_id });
        toast.success('Circuit added');
      }
      setCircuitDialog(false);
      setEditCircuitId(null);
      setCircuitForm({ location_name: '', installation_address: '', city: '', state: '', pincode: '', bandwidth: '', last_mile_type: 'Fiber', ip_allocation: 'Dynamic', monthly_charges: '', last_mile_contact_name: '', last_mile_contact_phone: '', latitude: '', longitude: '' });
      fetchData();
    } catch (error) {
      toast.error(editCircuitId ? 'Failed to update circuit' : 'Failed to add circuit');
    }
  };

  const handleEditCircuit = (circuit) => {
    setEditCircuitId(circuit.circuit_id);
    setCircuitForm({
      location_name: circuit.location_name || '',
      installation_address: circuit.installation_address || '',
      city: circuit.city || '',
      state: circuit.state || '',
      pincode: circuit.pincode || '',
      bandwidth: circuit.bandwidth || '',
      last_mile_type: circuit.last_mile_type || 'Fiber',
      ip_allocation: circuit.ip_allocation || 'Dynamic',
      monthly_charges: circuit.monthly_charges?.toString() || '',
      last_mile_contact_name: circuit.last_mile_contact_name || '',
      last_mile_contact_phone: circuit.last_mile_contact_phone || '',
      latitude: circuit.latitude?.toString() || '',
      longitude: circuit.longitude?.toString() || ''
    });
    setCircuitDialog(true);
  };

  const handleUpdateDeliveryData = async () => {
    if (!deliveryDialog.circuit) return;
    try {
      await axios.post(`${API}/circuits/${deliveryDialog.circuit.circuit_id}/update-delivery-data`, deliveryForm);
      toast.success('Delivery data updated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update delivery data');
    }
  };

  const handleDeliverCircuit = async () => {
    if (!deliveryDialog.circuit) return;
    if (!window.confirm('Confirm this circuit as Tested and Delivered? This action cannot be undone.')) return;
    try {
      await axios.post(`${API}/circuits/${deliveryDialog.circuit.circuit_id}/deliver`, deliveryForm);
      toast.success('Circuit marked as Tested and Delivered');
      setDeliveryDialog({ open: false, circuit: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deliver circuit');
    }
  };

  // Delete package
  const handleDeletePackage = async (packageId) => {
    if (!window.confirm('Are you sure you want to delete this package?')) return;
    try {
      await axios.delete(`${API}/packages/${packageId}`);
      toast.success('Package deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete package');
    }
  };

  // Delete circuit
  const handleDeleteCircuit = async (circuitId) => {
    if (!window.confirm('Are you sure you want to delete this circuit?')) return;
    try {
      await axios.delete(`${API}/circuits/${circuitId}`);
      toast.success('Circuit deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete circuit');
    }
  };

  // Customer edit
  const openEditCustomer = () => {
    setCustomerEditForm({
      company_name: customer.company_name || '',
      firm_type: customer.firm_type || '',
      registered_address: customer.registered_address || '',
      city: customer.city || '',
      state: customer.state || '',
      pincode: customer.pincode || '',
      contact_person: customer.contact_person || '',
      contact_designation: customer.contact_designation || '',
      contact_email: customer.contact_email || '',
      contact_phone: customer.contact_phone || '',
      gst_number: customer.gst_number || '',
      pan_number: customer.pan_number || '',
      territory: customer.territory || '',
      operator_share: customer.operator_share?.toString() || '',
      isp_share: customer.isp_share?.toString() || '',
      lco_management_enabled: customer.lco_management_enabled || false,
      iptv_access_token: customer.iptv_access_token || '',
      railtel_enabled: customer.railtel_enabled || false
    });
    setEditCustomerDialog(true);
  };

  // Payment Follow-up config
  const openFollowupConfig = async () => {
    setFollowupForm({
      recurring_amount: customer.recurring_amount?.toString() || '',
      recurring_cycle_days: customer.recurring_cycle_days || 30,
      recurring_next_due_date: customer.recurring_next_due_date || '',
      non_recurring_amount: customer.non_recurring_amount?.toString() || '',
      non_recurring_due_date: customer.non_recurring_due_date || '',
      payment_followup_assignee_email: customer.payment_followup_assignee_email || '',
    });
    // Lazy-load staff options (any logged-in employee can be assignee)
    if (staffOptions.length === 0) {
      try {
        const r = await axios.get(`${API}/tasks/assignees`);
        setStaffOptions(r.data || []);
      } catch {
        // Sales role can't hit /tasks/assignees — fall back to manual entry
      }
    }
    setFollowupDialog(true);
  };

  const saveFollowupConfig = async () => {
    setFollowupSaving(true);
    try {
      await axios.put(`${API}/payment-followup/customer/${customer_id}/config`, {
        recurring_amount: followupForm.recurring_amount === '' ? 0 : parseFloat(followupForm.recurring_amount),
        recurring_cycle_days: parseInt(followupForm.recurring_cycle_days, 10) || 30,
        recurring_next_due_date: followupForm.recurring_next_due_date || null,
        non_recurring_amount: followupForm.non_recurring_amount === '' ? 0 : parseFloat(followupForm.non_recurring_amount),
        non_recurring_due_date: followupForm.non_recurring_due_date || null,
        payment_followup_assignee_email: followupForm.payment_followup_assignee_email || null,
      });
      toast.success('Payment follow-up settings saved');
      setFollowupDialog(false);
      // Refresh customer
      const r = await axios.get(`${API}/customers/${customer_id}`);
      setCustomer(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setFollowupSaving(false);
    }
  };

  const handleUpdateCustomer = async () => {
    try {
      await axios.put(`${API}/customers/${customer_id}`, {
        ...customerEditForm,
        operator_share: customerEditForm.operator_share ? parseFloat(customerEditForm.operator_share) : null,
        isp_share: customerEditForm.isp_share ? parseFloat(customerEditForm.isp_share) : null,
        lco_management_enabled: !!customerEditForm.lco_management_enabled,
        railtel_enabled: !!customerEditForm.railtel_enabled
      });
      toast.success('Customer details updated');
      setEditCustomerDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update customer');
    }
  };

  // Document upload
  const handleUploadDoc = async () => {
    if (!docForm.document_url) {
      toast.error('Please upload a file first');
      return;
    }
    try {
      await axios.post(`${API}/customers/${customer_id}/documents`, docForm);
      toast.success('Document uploaded');
      setDocDialog(false);
      setDocForm({ document_type: 'pan_card', document_url: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to upload document');
    }
  };

  // Workflow actions
  const handleSubmitForApproval = async () => {
    try {
      await axios.post(`${API}/customers/${customer_id}/submit-for-approval`);
      toast.success('Submitted for approval');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit');
    }
  };

  const handleApprove = async () => {
    try {
      await axios.post(`${API}/customers/${customer_id}/approve-packages`);
      toast.success('Packages approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve');
    }
  };

  const handleRaisePO = async () => {
    if (!poProvider) {
      toast.error('Select a provider');
      return;
    }
    try {
      const response = await axios.post(`${API}/customers/${customer_id}/raise-po`, { provider: poProvider });
      toast.success(response.data.message);
      setPoDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to raise PO');
    }
  };

  const handleConfirmPO = async () => {
    try {
      await axios.post(`${API}/customers/${customer_id}/confirm-po`);
      toast.success('PO confirmed');
      fetchData();
    } catch (error) {
      toast.error('Failed to confirm PO');
    }
  };

  const handleMoveToDelivery = async () => {
    try {
      await axios.post(`${API}/customers/${customer_id}/move-to-delivery`);
      toast.success('Moved to delivery');
      fetchData();
    } catch (error) {
      toast.error('Failed to move to delivery');
    }
  };

  const handleDownloadAnnexure = async () => {
    try {
      // Open PDF in new tab - user can then save/print from there
      const token = localStorage.getItem('token');
      const url = `${API}/customers/${customer_id}/annexure-pdf`;
      
      // Fetch PDF and open in new window
      const response = await axios.get(url, {
        responseType: 'blob'
      });
      
      // Create blob URL and open in new tab
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      
      toast.success('PDF opened in new tab - you can save or print from there');
    } catch (error) {
      toast.error('Failed to generate annexure PDF');
    }
  };

  // Discussion/Notes functions
  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!noteForm.title.trim() || !noteForm.content.trim()) {
      toast.error('Please fill title and content');
      return;
    }
    try {
      await axios.post(`${API}/discussions`, {
        customer_id,
        ...noteForm
      });
      toast.success('Note added');
      setNoteDialog(false);
      setNoteForm({ note_type: 'general', title: '', content: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await axios.delete(`${API}/discussions/${noteId}`);
      toast.success('Note deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete note');
    }
  };

  const getNoteTypeInfo = (type) => {
    return NOTE_TYPES.find(t => t.value === type) || NOTE_TYPES[0];
  };

  if (loading) return <div className="flex items-center justify-center h-64">Loading...</div>;
  if (!customer) return null;

  const getStatusColor = (status) => {
    const colors = {
      onboarding: 'bg-slate-100 text-slate-700',
      packages_added: 'bg-blue-100 text-blue-700',
      approval_pending: 'bg-amber-100 text-amber-700',
      approved: 'bg-lime-100 text-lime-700',
      po_raised: 'bg-yellow-100 text-yellow-700',
      po_confirmed: 'bg-cyan-100 text-cyan-700',
      delivery_pending: 'bg-violet-100 text-violet-700',
      delivered: 'bg-sky-100 text-sky-700',
      active: 'bg-emerald-100 text-emerald-700'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="space-y-4 lg:space-y-6" data-testid="customer-detail-page">
      {/* Header - Mobile Responsive */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-start sm:items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/customers')} className="flex-shrink-0">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-xl lg:text-2xl font-semibold text-slate-900 truncate">{customer.company_name}</h2>
              <Badge className={getStatusColor(customer.workflow_status)}>
                {customer.workflow_status?.replace('_', ' ')}
              </Badge>
            </div>
            <p className="text-sm text-slate-600">
              <span className="font-mono">{customer.customer_code}</span> • {tierLabel(customer.customer_type)}
            </p>
          </div>
        </div>
        
        {/* Workflow Actions - Stack on Mobile */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/files?customer_id=${customer.customer_id}`)}
            className="flex-1 sm:flex-none text-sm"
            data-testid="customer-files-btn"
          >
            Files
          </Button>
          {['lco', 'iptv_distributor', 'partner'].includes(customer.customer_type) &&
           (user?.roles || (user?.role ? [user.role] : [])).some(r => ['admin', 'accounts'].includes(r)) && (
            <TierChangeButton customer={customer} API={API} onDone={() => window.location.reload()} />
          )}
          {['partner', 'bulk_bandwidth', 'iptv_distributor'].includes(customer.customer_type) &&
           user?.role === 'admin' && (
            <AuditVisibilityToggle customer={customer} API={API} />
          )}
          {customer.workflow_status === 'packages_added' && canEdit && (
            <Button onClick={handleSubmitForApproval} className="bg-amber-600 hover:bg-amber-700 flex-1 sm:flex-none text-sm">
              Submit for Approval
            </Button>
          )}
          {customer.workflow_status === 'approval_pending' && canApprove && (
            <Button onClick={handleApprove} className="bg-emerald-600 hover:bg-emerald-700 flex-1 sm:flex-none text-sm">
              <CheckCircle className="mr-1 h-4 w-4" /> Approve
            </Button>
          )}
          {customer.workflow_status === 'approved' && canRaisePO && (
            <Dialog open={poDialog} onOpenChange={setPoDialog}>
              <DialogTrigger asChild>
                <Button className="bg-sky-600 hover:bg-sky-700 flex-1 sm:flex-none text-sm">Raise PO</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Raise Purchase Order</DialogTitle>
                  <DialogDescription>Select provider for the PO</DialogDescription>
                </DialogHeader>
                <div className="py-4">
                  <Label>Provider *</Label>
                  <Select value={poProvider} onValueChange={setPoProvider}>
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Select provider" />
                    </SelectTrigger>
                    <SelectContent>
                      {PROVIDERS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter className="flex-col sm:flex-row gap-2">
                  <Button variant="outline" onClick={() => setPoDialog(false)} className="w-full sm:w-auto">Cancel</Button>
                  <Button onClick={handleRaisePO} className="w-full sm:w-auto">Raise PO</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          {customer.workflow_status === 'po_raised' && canRaisePO && (
            <Button onClick={handleConfirmPO} className="bg-emerald-600 hover:bg-emerald-700 flex-1 sm:flex-none text-sm">
              <CheckCircle className="mr-1 h-4 w-4" /> Confirm PO
            </Button>
          )}
          {customer.workflow_status === 'po_confirmed' && canRaisePO && (
            <Button onClick={handleMoveToDelivery} className="bg-violet-600 hover:bg-violet-700 flex-1 sm:flex-none text-sm">
              <Truck className="mr-1 h-4 w-4" /> To Delivery
            </Button>
          )}
          <Button variant="outline" onClick={handleDownloadAnnexure} className="text-sm">
            <Download className="mr-1 h-4 w-4" /> <span className="hidden sm:inline">View </span>PDF
          </Button>
        </div>
      </div>

      {/* Workflow Progress - Hidden on very small screens */}
      <Card className="hidden sm:block">
        <CardContent className="py-4">
          <div className="flex justify-between items-center">
            {['onboarding', 'packages_added', 'approval_pending', 'approved', 'po_raised', 'po_confirmed', 'delivery_pending', 'delivered', 'active'].map((status, idx) => {
              const statusList = ['onboarding', 'packages_added', 'approval_pending', 'approved', 'po_raised', 'po_confirmed', 'delivery_pending', 'delivered', 'active'];
              const currentIdx = statusList.indexOf(customer.workflow_status);
              const isComplete = idx <= currentIdx;
              const isCurrent = status === customer.workflow_status;
              return (
                <div key={status} className="flex-1 relative">
                  <div className={`h-2 ${idx === 0 ? 'rounded-l' : ''} ${idx === statusList.length - 1 ? 'rounded-r' : ''} ${isComplete ? 'bg-emerald-500' : 'bg-slate-200'}`} />
                  {isCurrent && (
                    <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-xs text-slate-600 whitespace-nowrap">
                      {status.replace('_', ' ')}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Mobile Workflow Status Badge */}
      <div className="sm:hidden">
        <Badge className={`${getStatusColor(customer.workflow_status)} text-sm px-3 py-1`}>
          Status: {customer.workflow_status?.replace('_', ' ')}
        </Badge>
      </div>

      <Tabs defaultValue="details" className="space-y-4">
        {/* Mobile-Scrollable Tabs */}
        <div className="overflow-x-auto -mx-4 px-4 lg:mx-0 lg:px-0">
          <TabsList className="inline-flex w-auto min-w-full lg:w-auto">
            <TabsTrigger value="details" className="text-sm">Details</TabsTrigger>
            <TabsTrigger value="packages" className="text-sm">Packages ({packages.length})</TabsTrigger>
            <TabsTrigger value="circuits" className="text-sm">Circuits ({circuits.length})</TabsTrigger>
            <TabsTrigger value="technical" className="text-sm">Technical</TabsTrigger>
            <TabsTrigger value="discussions" className="text-sm">Notes ({discussions.length})</TabsTrigger>
            {!isSupport && <TabsTrigger value="documents" className="text-sm">Docs</TabsTrigger>}
          </TabsList>
        </div>

        {/* Details Tab */}
        <TabsContent value="details">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <CardTitle className="flex items-center gap-2 text-base lg:text-lg">
                  <Building2 className="h-5 w-5" /> Company Info
                </CardTitle>
                {canEdit && (
                  <Button variant="outline" size="sm" onClick={openEditCustomer}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3 lg:gap-4">
                  <div><p className="text-slate-500 text-xs">Type</p><p className="font-medium">{tierLabel(customer.customer_type)}</p></div>
                  <div><p className="text-slate-500 text-xs">Firm Type</p><p className="font-medium capitalize">{customer.firm_type?.replace('_', ' ') || '-'}</p></div>
                  <div><p className="text-slate-500 text-xs">Contact</p><p className="font-medium truncate">{customer.contact_person}</p></div>
                  <div><p className="text-slate-500 text-xs">Phone</p><p className="font-medium">{customer.contact_phone}</p></div>
                  <div className="col-span-2"><p className="text-slate-500 text-xs">Email</p><p className="font-medium break-all">{customer.contact_email}</p></div>
                  <div className="col-span-2"><p className="text-slate-500 text-xs">Address</p><p className="font-medium">{customer.registered_address}, {customer.city}, {customer.state} - {customer.pincode}</p></div>
                  {customer.gst_number && <div className="col-span-2"><p className="text-slate-500 text-xs">GST</p><p className="font-medium font-mono text-xs">{customer.gst_number}</p></div>}
                  {customer.territory && <div className="col-span-2"><p className="text-slate-500 text-xs">Territory</p><p className="font-medium">{customer.territory}</p></div>}
                  {customer.customer_type === 'partner' && (
                    <div className="col-span-2">
                      <p className="text-slate-500 text-xs">LCO Management</p>
                      <p className={`font-medium text-sm ${customer.lco_management_enabled ? 'text-green-600' : 'text-slate-400'}`}>
                        {customer.lco_management_enabled ? 'Enabled' : 'Disabled'}
                      </p>
                    </div>
                  )}
                  <div className="col-span-2">
                    <p className="text-slate-500 text-xs">Railtel Wallet</p>
                    <p
                      className={`font-medium text-sm ${customer.railtel_enabled ? 'text-orange-600' : 'text-slate-400'}`}
                      data-testid="customer-railtel-status"
                    >
                      {customer.railtel_enabled ? 'Enabled' : 'Disabled'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base lg:text-lg">Commercial & PO</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3 lg:gap-4">
                  {customer.operator_share && <div><p className="text-slate-500 text-xs">Operator Share</p><p className="font-medium text-sky-600">{customer.operator_share}%</p></div>}
                  {customer.isp_share && <div><p className="text-slate-500 text-xs">ISP Share</p><p className="font-medium text-emerald-600">{customer.isp_share}%</p></div>}
                  <div><p className="text-slate-500 text-xs">Monthly</p><p className="font-medium text-lg">₹{customer.total_monthly_charges?.toLocaleString()}</p></div>
                  <div><p className="text-slate-500 text-xs">Contract</p><p className="font-medium">{customer.contract_period} months</p></div>
                  {customer.po_number && <div><p className="text-slate-500 text-xs">PO Number</p><p className="font-medium font-mono text-xs">{customer.po_number}</p></div>}
                  {customer.po_provider && <div><p className="text-slate-500 text-xs">Provider</p><p className="font-medium">{customer.po_provider}</p></div>}
                  {customer.po_confirmed && <div className="col-span-2"><p className="text-slate-500 text-xs">PO Status</p><p className="font-medium text-emerald-600"><CheckCircle className="inline mr-1 h-4 w-4" /> Confirmed</p></div>}
                </div>
              </CardContent>
            </Card>

            {/* Payment Follow-up settings */}
            <Card className="lg:col-span-2" data-testid="customer-payment-followup-card">
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <CardTitle className="flex items-center gap-2 text-base lg:text-lg">
                  <IndianRupee className="h-5 w-5" /> Payment Follow-up
                </CardTitle>
                {canEdit && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={openFollowupConfig}
                    data-testid="edit-payment-followup-btn"
                  >
                    <Pencil className="h-4 w-4 mr-1" /> Edit
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {/* Recurring stats */}
                {customer.recurring_amount > 0 && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-semibold text-emerald-700 text-sm">Recurring</p>
                      <span className="text-xs text-slate-500">Every {customer.recurring_cycle_days || 30} day(s)</span>
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-sm">
                      <div className="rounded-md border bg-slate-50 p-2">
                        <p className="text-[10px] text-slate-500 uppercase">Total</p>
                        <p className="font-semibold text-slate-900" data-testid="customer-recurring-amount">
                          ₹{Number(customer.recurring_amount).toLocaleString('en-IN')}
                        </p>
                      </div>
                      <div className="rounded-md border bg-blue-50 p-2">
                        <p className="text-[10px] text-blue-700 uppercase">Paid (cycle)</p>
                        <p className="font-semibold text-blue-700">
                          ₹{Number(customer.recurring_paid_current_cycle || 0).toLocaleString('en-IN')}
                        </p>
                      </div>
                      <div className="rounded-md border bg-amber-50 p-2">
                        <p className="text-[10px] text-amber-700 uppercase">Balance</p>
                        <p className="font-bold text-amber-700">
                          ₹{Math.max((customer.recurring_amount || 0) - (customer.recurring_paid_current_cycle || 0), 0).toLocaleString('en-IN')}
                        </p>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      <CalendarClock className="inline h-3 w-3 mr-1" />
                      Next Due: <span className="font-medium">{customer.recurring_next_due_date || '—'}</span>
                    </p>
                  </div>
                )}

                {/* Non-Recurring stats */}
                {customer.non_recurring_amount > 0 && (
                  <div className="mb-2">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-semibold text-amber-800 text-sm">Non-Recurring (one-off)</p>
                      {customer.non_recurring_cleared && (
                        <Badge className="bg-emerald-100 text-emerald-700">
                          <CheckCircle className="h-3 w-3 mr-1" /> Cleared
                        </Badge>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-sm">
                      <div className="rounded-md border bg-slate-50 p-2">
                        <p className="text-[10px] text-slate-500 uppercase">Total</p>
                        <p className="font-semibold text-slate-900" data-testid="customer-non-recurring-amount">
                          ₹{Number(customer.non_recurring_amount).toLocaleString('en-IN')}
                        </p>
                      </div>
                      <div className="rounded-md border bg-blue-50 p-2">
                        <p className="text-[10px] text-blue-700 uppercase">Paid</p>
                        <p className="font-semibold text-blue-700">
                          ₹{Number(customer.non_recurring_paid || 0).toLocaleString('en-IN')}
                        </p>
                      </div>
                      <div className="rounded-md border bg-amber-50 p-2">
                        <p className="text-[10px] text-amber-700 uppercase">Balance</p>
                        <p className="font-bold text-amber-700">
                          ₹{Math.max((customer.non_recurring_amount || 0) - (customer.non_recurring_paid || 0), 0).toLocaleString('en-IN')}
                        </p>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      <CalendarClock className="inline h-3 w-3 mr-1" />
                      Due Date: <span className="font-medium">{customer.non_recurring_due_date || '—'}</span>
                    </p>
                  </div>
                )}

                {/* Empty state */}
                {!customer.recurring_amount && !customer.non_recurring_amount && (
                  <p className="text-sm text-slate-500 py-4 text-center">
                    No payment follow-up amounts configured. Click <span className="font-medium">Edit</span> to add.
                  </p>
                )}

                {/* Assignee */}
                <div className="mt-3 pt-3 border-t flex items-center justify-between text-xs">
                  <div>
                    <span className="text-slate-500">Assigned to: </span>
                    <span className="font-medium text-slate-800">
                      {customer.payment_followup_assignee_email || 'Not assigned'}
                    </span>
                  </div>
                  <span className="text-slate-400">
                    Surfaces in Payments → Follow-up
                  </span>
                </div>
              </CardContent>
            </Card>

            <IPTVApiAccessCard customer={customer} API={API} canEdit={canEdit} />
          </div>
        </TabsContent>
        <TabsContent value="packages">
          <Card>
            <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3">
              <div>
                <CardTitle className="flex items-center gap-2 text-base lg:text-lg"><Package className="h-5 w-5" /> Packages</CardTitle>
                <CardDescription className="text-sm">Service packages for this customer</CardDescription>
              </div>
              {canEdit && !customer.packages_approved && (
                <Dialog open={packageDialog} onOpenChange={(open) => { setPackageDialog(open); if (!open) { setEditPackageId(null); setPackageForm({ package_name: '', service_type: 'broadband', speed: '', includes_tv: false, includes_ott: false, ott_platforms: '', tv_channels: '', mrp: '', minimum_guarantee: '' }); } }}>
                  <DialogTrigger asChild>
                    <Button className="bg-slate-900 hover:bg-slate-800 w-full sm:w-auto"><Plus className="mr-2 h-4 w-4" /> Add Package</Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>{editPackageId ? 'Edit Package' : 'Add Package'}</DialogTitle>
                      <DialogDescription>{editPackageId ? 'Update package details' : 'Add a new service package'}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Package Name *</Label>
                          <Input value={packageForm.package_name} onChange={(e) => setPackageForm({ ...packageForm, package_name: e.target.value })} placeholder="e.g., Premium 100" />
                        </div>
                        <div className="space-y-2">
                          <Label>Service Type</Label>
                          <Select value={packageForm.service_type} onValueChange={(v) => setPackageForm({ ...packageForm, service_type: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="broadband">Broadband</SelectItem>
                              <SelectItem value="broadband_tv">Broadband + TV</SelectItem>
                              <SelectItem value="broadband_ott">Broadband + OTT</SelectItem>
                              <SelectItem value="triple_play">Triple Play</SelectItem>
                              <SelectItem value="iptv_only">IPTV Only</SelectItem>
                              <SelectItem value="ott_only">OTT Only</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Speed (Mbps)</Label>
                          <Input value={packageForm.speed} onChange={(e) => setPackageForm({ ...packageForm, speed: e.target.value })} placeholder="e.g., 50, 75, 100" />
                        </div>
                        <div className="space-y-2">
                          <Label>MRP (₹ incl. 18% GST) *</Label>
                          <Input type="number" value={packageForm.mrp} onChange={(e) => setPackageForm({ ...packageForm, mrp: e.target.value })} />
                        </div>
                      </div>
                      <div className="p-3 bg-sky-50 rounded-lg border border-sky-200">
                        <Label className="text-sky-800 font-semibold mb-2 block text-sm">Revenue Sharing</Label>
                        <p className="text-xs text-sky-600 mb-3">Based on base price (MRP ÷ 1.18)</p>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-xs">Operator Share (%)</Label>
                            <Input type="number" step="0.1" value={packageForm.operator_share_percent} onChange={(e) => setPackageForm({ ...packageForm, operator_share_percent: e.target.value })} placeholder="70" />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs">ISP Share (%)</Label>
                            <Input type="number" step="0.1" value={packageForm.isp_share_percent} onChange={(e) => setPackageForm({ ...packageForm, isp_share_percent: e.target.value })} placeholder="30" />
                          </div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Minimum Guarantee (₹)</Label>
                        <Input type="number" value={packageForm.minimum_guarantee} onChange={(e) => setPackageForm({ ...packageForm, minimum_guarantee: e.target.value })} placeholder="Optional" />
                      </div>
                    </div>
                    <DialogFooter className="flex-col sm:flex-row gap-2">
                      <Button variant="outline" onClick={() => { setPackageDialog(false); setEditPackageId(null); }} className="w-full sm:w-auto">Cancel</Button>
                      <Button onClick={handleAddPackage} className="w-full sm:w-auto">{editPackageId ? 'Update' : 'Add Package'}</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
            </CardHeader>
            <CardContent className="p-0">
              {packages.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Package className="mx-auto h-10 w-10 text-slate-300 mb-2" />
                  <p>No packages configured</p>
                </div>
              ) : (
                <>
                  {/* Mobile View - Cards */}
                  <div className="lg:hidden p-4 space-y-3">
                    {packages.map((pkg) => (
                      <div key={pkg.package_id} className="bg-slate-50 rounded-lg p-4 space-y-2">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-slate-900">{pkg.package_name}</p>
                            <p className="text-sm text-slate-600 capitalize">{pkg.service_type?.replace('_', ' ')}</p>
                          </div>
                          {canEdit && (
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" onClick={() => handleEditPackage(pkg)}><Pencil className="h-4 w-4" /></Button>
                              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeletePackage(pkg.package_id)}><Trash2 className="h-4 w-4" /></Button>
                            </div>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div><span className="text-slate-500">Speed:</span> {pkg.speed || '-'} Mbps</div>
                          <div><span className="text-slate-500">MRP:</span> ₹{pkg.mrp?.toLocaleString()}</div>
                          <div className="text-sky-600"><span className="text-slate-500">Op:</span> ₹{pkg.operator_commission?.toLocaleString() || '-'} ({pkg.operator_share_percent || 0}%)</div>
                          <div className="text-emerald-600"><span className="text-slate-500">ISP:</span> ₹{pkg.isp_revenue?.toLocaleString() || '-'} ({pkg.isp_share_percent || 0}%)</div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Desktop View - Table */}
                  <div className="hidden lg:block overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>Package</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Speed</TableHead>
                          <TableHead>MRP</TableHead>
                          <TableHead>Base Price</TableHead>
                          <TableHead>Op. Share</TableHead>
                          <TableHead>ISP Share</TableHead>
                          {canEdit && <TableHead>Actions</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {packages.map((pkg) => (
                          <TableRow key={pkg.package_id}>
                            <TableCell className="font-medium">{pkg.package_name}</TableCell>
                            <TableCell className="capitalize">{pkg.service_type?.replace('_', ' ')}</TableCell>
                            <TableCell>{pkg.speed ? `${pkg.speed} Mbps` : '-'}</TableCell>
                            <TableCell>₹{pkg.mrp?.toLocaleString()}</TableCell>
                            <TableCell className="text-slate-600">₹{pkg.base_price?.toLocaleString() || (pkg.mrp / 1.18).toFixed(2)}</TableCell>
                            <TableCell className="text-sky-600">
                              <div>
                                <span className="font-medium">₹{pkg.operator_commission?.toLocaleString() || '-'}</span>
                                <span className="text-xs text-slate-400 ml-1">({pkg.operator_share_percent || 0}%)</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-emerald-600">
                              <div>
                                <span className="font-medium">₹{pkg.isp_revenue?.toLocaleString() || '-'}</span>
                                <span className="text-xs text-slate-400 ml-1">({pkg.isp_share_percent || 0}%)</span>
                              </div>
                            </TableCell>
                            {canEdit && (
                              <TableCell>
                                <div className="flex gap-1">
                                  <Button size="sm" variant="ghost" className="text-slate-600" onClick={() => handleEditPackage(pkg)}><Pencil className="h-4 w-4" /></Button>
                                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeletePackage(pkg.package_id)}><Trash2 className="h-4 w-4" /></Button>
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Circuits Tab */}
        <TabsContent value="circuits">
          <Card>
            <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3">
              <div>
                <CardTitle className="flex items-center gap-2 text-base lg:text-lg"><Network className="h-5 w-5" /> Circuits</CardTitle>
                <CardDescription className="text-sm">Circuit delivery and configuration</CardDescription>
              </div>
              {(canEdit || canDeliver) && (
                <Dialog open={circuitDialog} onOpenChange={(open) => { setCircuitDialog(open); if (!open) { setEditCircuitId(null); setCircuitForm({ location_name: '', installation_address: '', city: '', state: '', pincode: '', bandwidth: '', last_mile_type: 'Fiber', ip_allocation: 'Dynamic', monthly_charges: '', last_mile_contact_name: '', last_mile_contact_phone: '', latitude: '', longitude: '' }); } }}>
                  <DialogTrigger asChild>
                    <Button className="bg-slate-900 hover:bg-slate-800 w-full sm:w-auto"><Plus className="mr-2 h-4 w-4" /> Add Circuit</Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>{editCircuitId ? 'Edit Circuit' : 'Add Circuit'}</DialogTitle>
                      <DialogDescription>{editCircuitId ? 'Update circuit details' : 'Add a new circuit location'}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Location Name *</Label>
                          <Input value={circuitForm.location_name} onChange={(e) => setCircuitForm({ ...circuitForm, location_name: e.target.value })} placeholder="e.g., Head Office" />
                        </div>
                        <div className="space-y-2">
                          <Label>Bandwidth (Mbps) *</Label>
                          <Input value={circuitForm.bandwidth} onChange={(e) => setCircuitForm({ ...circuitForm, bandwidth: e.target.value })} placeholder="e.g., 50, 75, 100" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Installation Address *</Label>
                        <Textarea value={circuitForm.installation_address} onChange={(e) => setCircuitForm({ ...circuitForm, installation_address: e.target.value })} rows={2} />
                      </div>
                      <div className="grid grid-cols-3 gap-2 sm:gap-4">
                        <div className="space-y-2"><Label className="text-xs sm:text-sm">City *</Label><Input value={circuitForm.city} onChange={(e) => setCircuitForm({ ...circuitForm, city: e.target.value })} /></div>
                        <div className="space-y-2"><Label className="text-xs sm:text-sm">State *</Label><Input value={circuitForm.state} onChange={(e) => setCircuitForm({ ...circuitForm, state: e.target.value })} /></div>
                        <div className="space-y-2"><Label className="text-xs sm:text-sm">Pincode *</Label><Input value={circuitForm.pincode} onChange={(e) => setCircuitForm({ ...circuitForm, pincode: e.target.value })} /></div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Monthly Charges (₹) *</Label>
                          <Input type="number" value={circuitForm.monthly_charges} onChange={(e) => setCircuitForm({ ...circuitForm, monthly_charges: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                          <Label>Last Mile Type</Label>
                          <Select value={circuitForm.last_mile_type} onValueChange={(v) => setCircuitForm({ ...circuitForm, last_mile_type: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Fiber">Fiber</SelectItem>
                              <SelectItem value="Wireless">Wireless</SelectItem>
                              <SelectItem value="Copper">Copper</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                        <Label className="text-emerald-800 font-semibold mb-2 block text-sm">Location (for Map)</Label>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-xs">Latitude</Label>
                            <Input type="number" step="0.000001" value={circuitForm.latitude} onChange={(e) => setCircuitForm({ ...circuitForm, latitude: e.target.value })} placeholder="12.9716" />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs">Longitude</Label>
                            <Input type="number" step="0.000001" value={circuitForm.longitude} onChange={(e) => setCircuitForm({ ...circuitForm, longitude: e.target.value })} placeholder="77.5946" />
                          </div>
                        </div>
                      </div>
                    </div>
                    <DialogFooter className="flex-col sm:flex-row gap-2">
                      <Button variant="outline" onClick={() => { setCircuitDialog(false); setEditCircuitId(null); }} className="w-full sm:w-auto">Cancel</Button>
                      <Button onClick={handleAddCircuit} className="w-full sm:w-auto">{editCircuitId ? 'Update' : 'Add Circuit'}</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
            </CardHeader>
            <CardContent className="p-0 lg:p-4 lg:pt-0">
              {circuits.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Network className="mx-auto h-10 w-10 text-slate-300 mb-2" />
                  <p>No circuits configured yet</p>
                </div>
              ) : (
                <div className="divide-y">
                  {circuits.map((circuit) => (
                    <div key={circuit.circuit_id} className="p-4 hover:bg-slate-50/50">
                      {/* Circuit Header - Mobile Responsive */}
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                          <div className="bg-sky-100 px-3 py-2 rounded-lg inline-block">
                            <p className="text-xs text-sky-600 font-medium">ANT Circuit ID</p>
                            <p className="font-mono font-bold text-sky-800 text-base lg:text-lg">{circuit.ant_circuit_id}</p>
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900">{circuit.location_name}</p>
                            <p className="text-sm text-slate-500">{circuit.bandwidth} Mbps • {circuit.last_mile_type} • {circuit.city}</p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-3">
                          <div className="text-left sm:text-right">
                            <p className="text-xs text-slate-500">Monthly</p>
                            <p className="font-semibold text-slate-900">₹{circuit.monthly_charges?.toLocaleString()}</p>
                          </div>
                          <Badge className={circuit.is_delivered ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                            {circuit.is_delivered ? 'Delivered' : 'Pending'}
                          </Badge>
                        </div>
                      </div>

                      {/* Delivery Details (if delivered) */}
                      {circuit.is_delivered && (
                        <div className="bg-emerald-50 rounded-lg p-4 mt-3 border border-emerald-200">
                          <p className="text-sm font-semibold text-emerald-800 mb-3 flex items-center gap-2">
                            <CheckCircle className="h-4 w-4" /> Delivery Details
                          </p>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <p className="text-emerald-600 text-xs">Provider</p>
                              <p className="font-medium text-slate-800">{circuit.provider || '-'}</p>
                            </div>
                            <div>
                              <p className="text-emerald-600 text-xs">Provider Circuit ID</p>
                              <p className="font-mono text-slate-800">{circuit.provider_circuit_id || '-'}</p>
                            </div>
                            <div>
                              <p className="text-emerald-600 text-xs">RA Number</p>
                              <p className="font-mono text-slate-800">{circuit.ra_number || '-'}</p>
                            </div>
                            <div>
                              <p className="text-emerald-600 text-xs">VLAN ID</p>
                              <p className="font-mono text-slate-800">{circuit.vlan_id || '-'}</p>
                            </div>
                            <div className="col-span-2">
                              <p className="text-emerald-600 text-xs">End A (ISP Side)</p>
                              <p className="text-slate-800">{circuit.end_a_port_details || '-'}</p>
                            </div>
                            <div className="col-span-2">
                              <p className="text-emerald-600 text-xs">End B (Customer Side)</p>
                              <p className="text-slate-800">{circuit.end_b_port_details || '-'}</p>
                            </div>
                            <div>
                              <p className="text-emerald-600 text-xs">Delivery Date</p>
                              <p className="text-slate-800">{circuit.delivery_date || '-'}</p>
                            </div>
                            <div>
                              <p className="text-emerald-600 text-xs">Delivered By</p>
                              <p className="text-slate-800">{circuit.delivered_by || '-'}</p>
                            </div>
                            {circuit.latency_ms && (
                              <div>
                                <p className="text-emerald-600 text-xs">Measured Latency</p>
                                <p className="text-slate-800 font-mono">{circuit.latency_ms} ms</p>
                              </div>
                            )}
                            {circuit.speedtest_image && (
                              <div className="col-span-2">
                                <p className="text-emerald-600 text-xs mb-1">Speedtest Screenshot</p>
                                <a
                                  href={`data:${circuit.speedtest_image_mime || 'image/png'};base64,${circuit.speedtest_image}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-block"
                                >
                                  <img
                                    src={`data:${circuit.speedtest_image_mime || 'image/png'};base64,${circuit.speedtest_image}`}
                                    alt="Speedtest"
                                    className="max-w-xs rounded border border-emerald-200 hover:opacity-90 transition"
                                  />
                                </a>
                              </div>
                            )}
                            {circuit.delivery_notes && (
                              <div className="col-span-2">
                                <p className="text-emerald-600 text-xs">Delivery Notes</p>
                                <p className="text-slate-800">{circuit.delivery_notes}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Action Buttons - Mobile Responsive */}
                      <div className="mt-3 flex flex-wrap justify-end gap-2">
                        {canRaisePO && (
                          <Button size="sm" variant="outline" className="text-indigo-600 hover:bg-indigo-50 flex-1 sm:flex-none"
                            onClick={() => navigate(`/purchase-orders?customer=${customer_id}&circuit=${circuit.circuit_id}&bandwidth=${circuit.bandwidth}`)}>
                            <ShoppingCart className="mr-1 h-4 w-4" /> <span className="hidden sm:inline">Create </span>PO
                          </Button>
                        )}
                        {canEdit && (
                          <Button size="sm" variant="outline" onClick={() => handleEditCircuit(circuit)} className="flex-1 sm:flex-none">
                            <Pencil className="mr-1 h-4 w-4" /> Edit
                          </Button>
                        )}
                        {canEdit && (
                          <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50"
                            onClick={() => handleDeleteCircuit(circuit.circuit_id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                        {!circuit.is_delivered && canDeliver && (
                          <Button size="sm" onClick={() => {
                            setDeliveryForm({
                              provider: circuit.provider || '',
                              provider_circuit_id: circuit.provider_circuit_id || '',
                              ra_number: circuit.ra_number || '',
                              end_a_port_details: circuit.end_a_port_details || '',
                              end_b_port_details: circuit.end_b_port_details || '',
                              vlan_id: circuit.vlan_id || '',
                              last_mile_contact_name: circuit.last_mile_contact_name || '',
                              last_mile_contact_phone: circuit.last_mile_contact_phone || '',
                              delivery_notes: circuit.delivery_notes || '',
                              accounts_notes: circuit.accounts_notes || '',
                              delivery_date: circuit.delivery_date || new Date().toISOString().split('T')[0],
                              latency_ms: circuit.latency_ms || '',
                              speedtest_image: circuit.speedtest_image || '',
                              speedtest_image_filename: circuit.speedtest_image_filename || '',
                              speedtest_image_mime: circuit.speedtest_image_mime || ''
                            });
                            setDeliveryDialog({ open: true, circuit });
                          }} className="bg-emerald-600 hover:bg-emerald-700 flex-1 sm:flex-none">
                            <Truck className="mr-1 h-4 w-4" /> Deliver
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Technical Implementation Tab */}
        <TabsContent value="technical">
          <Card>
            <CardContent className="pt-6">
              <TechnicalImplementation customerId={customer_id} customerName={customer.company_name} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Discussions Tab */}
        <TabsContent value="discussions">
          <Card>
            <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3">
              <div>
                <CardTitle className="flex items-center gap-2 text-base lg:text-lg">
                  <MessageSquare className="h-5 w-5" />
                  Notes
                </CardTitle>
                <CardDescription className="text-sm">Discussions, negotiations & requirements</CardDescription>
              </div>
              <Dialog open={noteDialog} onOpenChange={setNoteDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-slate-900 hover:bg-slate-800 w-full sm:w-auto">
                    <Plus className="mr-2 h-4 w-4" /> Add Note
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Add Discussion Note</DialogTitle>
                    <DialogDescription className="text-sm">Add a note about negotiations, requirements, or meeting outcomes</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleAddNote} className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-sm">Note Type</Label>
                      <Select value={noteForm.note_type} onValueChange={(v) => setNoteForm({...noteForm, note_type: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {NOTE_TYPES.map(t => (
                            <SelectItem key={t.value} value={t.value}>
                              <div className="flex items-center gap-2">
                                <Badge className={t.color}>{t.label}</Badge>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">Title *</Label>
                      <Input 
                        value={noteForm.title}
                        onChange={(e) => setNoteForm({...noteForm, title: e.target.value})}
                        placeholder="Brief summary"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">Content *</Label>
                      <Textarea
                        value={noteForm.content}
                        onChange={(e) => setNoteForm({...noteForm, content: e.target.value})}
                        placeholder="Details, requirements, or action items..."
                        rows={4}
                        required
                      />
                    </div>
                    <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800">
                      <Send className="mr-2 h-4 w-4" /> Add Note
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent className="p-0 lg:p-6 lg:pt-0">
              {discussions.length === 0 ? (
                <div className="text-center py-8 text-slate-500 px-4">
                  <MessageSquare className="mx-auto h-10 w-10 text-slate-300 mb-2" />
                  <p className="font-medium">No notes yet</p>
                  <p className="text-sm">Add notes about negotiations or meetings</p>
                </div>
              ) : (
                <div className="divide-y">
                  {discussions.map((note) => {
                    const typeInfo = getNoteTypeInfo(note.note_type);
                    return (
                      <div key={note.note_id} className="p-4 hover:bg-slate-50 transition-colors">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge className={typeInfo.color}>{typeInfo.label}</Badge>
                            <span className="font-medium text-slate-900 text-sm">{note.title}</span>
                          </div>
                          <div className="flex items-center justify-between sm:justify-end gap-2">
                            <span className="text-xs text-slate-500">
                              {new Date(note.created_at).toLocaleDateString('en-IN', { 
                                day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                              })}
                            </span>
                            {(note.created_by === user?.email || user?.role === 'admin') && (
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="text-red-500 hover:text-red-700 h-7 w-7 p-0"
                                onClick={() => handleDeleteNote(note.note_id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                        <p className="text-slate-600 whitespace-pre-wrap text-sm">{note.content}</p>
                        <div className="mt-2 pt-2 border-t text-xs text-slate-500">
                          By: {note.created_by_name || note.created_by}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        {!isSupport && (
          <TabsContent value="documents">
            <Card>
              <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3">
                <div>
                  <CardTitle className="text-base lg:text-lg">Documents</CardTitle>
                  <CardDescription className="text-sm">KYC and Agreement documents</CardDescription>
                </div>
                {canEdit && (
                  <Dialog open={docDialog} onOpenChange={setDocDialog}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="w-full sm:w-auto"><Upload className="mr-2 h-4 w-4" /> Upload</Button>
                    </DialogTrigger>
                    <DialogContent className="max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Upload Document</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label className="text-sm">Document Type</Label>
                          <Select value={docForm.document_type} onValueChange={(v) => setDocForm({ ...docForm, document_type: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pan_card">PAN Card</SelectItem>
                              <SelectItem value="gst_certificate">GST Certificate</SelectItem>
                              <SelectItem value="incorporation">Incorporation Certificate</SelectItem>
                              <SelectItem value="agreement">Agreement</SelectItem>
                              <SelectItem value="address_proof">Address Proof</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm">Upload File</Label>
                          <div className="border-2 border-dashed rounded-lg p-4 text-center">
                            <input type="file" ref={fileInputRef} onChange={handleFileUpload} className="hidden" accept=".pdf,.jpg,.jpeg,.png" />
                            {docForm.document_url ? (
                              <p className="text-emerald-600 text-sm"><CheckCircle className="inline mr-2 h-4 w-4" />Uploaded</p>
                            ) : (
                              <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                                {uploading ? 'Uploading...' : 'Choose File'}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                      <DialogFooter className="flex-col sm:flex-row gap-2">
                        <Button variant="outline" onClick={() => setDocDialog(false)} className="w-full sm:w-auto">Cancel</Button>
                        <Button onClick={handleUploadDoc} disabled={!docForm.document_url} className="w-full sm:w-auto">Save</Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                )}
              </CardHeader>
              <CardContent className="p-0 lg:p-6 lg:pt-0">
                {customer.kyc_documents?.length === 0 && !customer.agreement_document ? (
                  <div className="text-center py-8 text-slate-500 px-4">
                    <FileText className="mx-auto h-10 w-10 text-slate-300 mb-2" />
                    <p>No documents uploaded</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {customer.agreement_document && (
                      <div className="flex items-center justify-between p-4 bg-sky-50/50 gap-2">
                        <div className="flex items-center gap-3 min-w-0">
                          <FileText className="h-8 w-8 text-sky-600 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="font-medium text-sm">Agreement</p>
                            <p className="text-xs text-slate-500 truncate">{customer.agreement_upload_date?.split('T')[0]}</p>
                          </div>
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          <Button variant="outline" size="sm" asChild>
                            <a href={`${API.replace('/api', '')}${customer.agreement_document}`} target="_blank" rel="noopener noreferrer">View</a>
                          </Button>
                          {canEdit && (
                            <>
                              <Button
                                variant="outline" size="sm"
                                onClick={() => { setDocForm({ document_type: 'agreement', document_url: '' }); setDocDialog(true); }}
                                data-testid="reupload-agreement-btn"
                                title="Replace"
                              >Replace</Button>
                              <Button
                                variant="outline" size="sm"
                                className="text-red-600 hover:bg-red-50 border-red-200"
                                onClick={async () => {
                                  if (!window.confirm('Delete agreement document?')) return;
                                  try {
                                    await axios.delete(`${API}/customers/${customer.customer_id}/documents/agreement`);
                                    toast.success('Agreement removed');
                                    fetchData();
                                  } catch { toast.error('Failed to delete'); }
                                }}
                                data-testid="delete-agreement-btn"
                                title="Delete"
                              ><Trash2 className="h-4 w-4" /></Button>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                    {customer.kyc_documents?.map((doc, idx) => (
                      <div key={idx} className="flex items-center justify-between p-4 gap-2">
                        <div className="flex items-center gap-3 min-w-0">
                          <FileText className="h-8 w-8 text-slate-400 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="font-medium capitalize text-sm">{doc.document_type?.replace('_', ' ')}</p>
                            <p className="text-xs text-slate-500 truncate">{doc.uploaded_at?.split('T')[0]}</p>
                          </div>
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          <Button variant="outline" size="sm" asChild>
                            <a href={`${API.replace('/api', '')}${doc.document_url}`} target="_blank" rel="noopener noreferrer">View</a>
                          </Button>
                          {canEdit && (
                            <>
                              <Button
                                variant="outline" size="sm"
                                onClick={async () => {
                                  if (!window.confirm(`Delete this ${doc.document_type?.replace('_', ' ')} and re-upload? Click OK to delete now.`)) return;
                                  try {
                                    await axios.delete(`${API}/customers/${customer.customer_id}/documents/${idx}`);
                                    toast.success('Removed — opening upload dialog');
                                    await fetchData();
                                    setDocForm({ document_type: doc.document_type, document_url: '' });
                                    setDocDialog(true);
                                  } catch { toast.error('Failed to delete'); }
                                }}
                                data-testid={`reupload-doc-${idx}`}
                                title="Replace"
                              >Replace</Button>
                              <Button
                                variant="outline" size="sm"
                                className="text-red-600 hover:bg-red-50 border-red-200"
                                onClick={async () => {
                                  if (!window.confirm(`Delete this ${doc.document_type?.replace('_', ' ')}?`)) return;
                                  try {
                                    await axios.delete(`${API}/customers/${customer.customer_id}/documents/${idx}`);
                                    toast.success('Document removed');
                                    fetchData();
                                  } catch { toast.error('Failed to delete'); }
                                }}
                                data-testid={`delete-doc-${idx}`}
                                title="Delete"
                              ><Trash2 className="h-4 w-4" /></Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Delivery Dialog */}
      <Dialog open={deliveryDialog.open} onOpenChange={(open) => setDeliveryDialog({ ...deliveryDialog, open })}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Deliver Circuit</DialogTitle>
            <DialogDescription>ANT Circuit: {deliveryDialog.circuit?.ant_circuit_id}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm">Provider *</Label>
                <Select value={deliveryForm.provider} onValueChange={(v) => setDeliveryForm({ ...deliveryForm, provider: v })}>
                  <SelectTrigger><SelectValue placeholder="Select provider" /></SelectTrigger>
                  <SelectContent>
                    {PROVIDERS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Provider Circuit ID</Label>
                <Input value={deliveryForm.provider_circuit_id} onChange={(e) => setDeliveryForm({ ...deliveryForm, provider_circuit_id: e.target.value })} placeholder="Circuit ID from provider" />
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label className="text-xs sm:text-sm">RA Number</Label>
                <Input value={deliveryForm.ra_number} onChange={(e) => setDeliveryForm({ ...deliveryForm, ra_number: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label className="text-xs sm:text-sm">VLAN ID</Label>
                <Input value={deliveryForm.vlan_id} onChange={(e) => setDeliveryForm({ ...deliveryForm, vlan_id: e.target.value })} placeholder="e.g., 100" />
              </div>
              <div className="space-y-2 col-span-2 sm:col-span-1">
                <Label className="text-xs sm:text-sm">Delivery Date</Label>
                <Input type="date" value={deliveryForm.delivery_date} onChange={(e) => setDeliveryForm({ ...deliveryForm, delivery_date: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm">End A (ISP Side)</Label>
                <Textarea value={deliveryForm.end_a_port_details} onChange={(e) => setDeliveryForm({ ...deliveryForm, end_a_port_details: e.target.value })} rows={2} placeholder="POP, Switch, Port" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm">End B (Customer)</Label>
                <Textarea value={deliveryForm.end_b_port_details} onChange={(e) => setDeliveryForm({ ...deliveryForm, end_b_port_details: e.target.value })} rows={2} placeholder="OLT, Port" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-sm">Measured Latency (ms)</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  value={deliveryForm.latency_ms}
                  onChange={(e) => setDeliveryForm({ ...deliveryForm, latency_ms: e.target.value })}
                  placeholder="e.g., 12.5"
                  data-testid="delivery-latency-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Speedtest Screenshot</Label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (file.size > 5 * 1024 * 1024) {
                      toast.error('Image must be under 5 MB');
                      e.target.value = '';
                      return;
                    }
                    const reader = new FileReader();
                    reader.onload = () => {
                      const result = String(reader.result || '');
                      const base64 = result.split(',')[1] || result;
                      setDeliveryForm(prev => ({
                        ...prev,
                        speedtest_image: base64,
                        speedtest_image_filename: file.name,
                        speedtest_image_mime: file.type || 'image/png',
                      }));
                    };
                    reader.readAsDataURL(file);
                  }}
                  data-testid="speedtest-image-input"
                />
                {deliveryForm.speedtest_image && (
                  <div className="flex items-center gap-2 text-xs text-emerald-700">
                    <CheckCircle className="h-3 w-3" />
                    <span className="truncate">{deliveryForm.speedtest_image_filename || 'Image attached'}</span>
                    <button
                      type="button"
                      className="text-red-600 hover:underline ml-auto"
                      onClick={() => setDeliveryForm({ ...deliveryForm, speedtest_image: '', speedtest_image_filename: '', speedtest_image_mime: '' })}
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Delivery Notes</Label>
              <Textarea value={deliveryForm.delivery_notes} onChange={(e) => setDeliveryForm({ ...deliveryForm, delivery_notes: e.target.value })} rows={2} placeholder="Additional notes" />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setDeliveryDialog({ open: false, circuit: null })} className="w-full sm:w-auto">Cancel</Button>
            <Button onClick={handleUpdateDeliveryData} variant="outline" className="w-full sm:w-auto border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="update-delivery-data-btn">
              <RefreshCw className="mr-1 h-4 w-4" /> Update Data
            </Button>
            <Button onClick={handleDeliverCircuit} className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto" data-testid="tested-delivered-btn">
              <CheckCircle className="mr-1 h-4 w-4" /> Tested and Delivered
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Customer Dialog */}
      <Dialog open={editCustomerDialog} onOpenChange={setEditCustomerDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Customer Details</DialogTitle>
            <DialogDescription>Update customer information</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Company Name *</Label>
                <Input value={customerEditForm.company_name} onChange={(e) => setCustomerEditForm({ ...customerEditForm, company_name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Firm Type</Label>
                <Input value={customerEditForm.firm_type} onChange={(e) => setCustomerEditForm({ ...customerEditForm, firm_type: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Registered Address</Label>
              <Textarea value={customerEditForm.registered_address} onChange={(e) => setCustomerEditForm({ ...customerEditForm, registered_address: e.target.value })} rows={2} />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>City</Label>
                <Input value={customerEditForm.city} onChange={(e) => setCustomerEditForm({ ...customerEditForm, city: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>State</Label>
                <Input value={customerEditForm.state} onChange={(e) => setCustomerEditForm({ ...customerEditForm, state: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Pincode</Label>
                <Input value={customerEditForm.pincode} onChange={(e) => setCustomerEditForm({ ...customerEditForm, pincode: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Contact Person</Label>
                <Input value={customerEditForm.contact_person} onChange={(e) => setCustomerEditForm({ ...customerEditForm, contact_person: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Designation</Label>
                <Input value={customerEditForm.contact_designation} onChange={(e) => setCustomerEditForm({ ...customerEditForm, contact_designation: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={customerEditForm.contact_email} onChange={(e) => setCustomerEditForm({ ...customerEditForm, contact_email: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={customerEditForm.contact_phone} onChange={(e) => setCustomerEditForm({ ...customerEditForm, contact_phone: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>GST Number</Label>
                <Input value={customerEditForm.gst_number} onChange={(e) => setCustomerEditForm({ ...customerEditForm, gst_number: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>PAN Number</Label>
                <Input value={customerEditForm.pan_number} onChange={(e) => setCustomerEditForm({ ...customerEditForm, pan_number: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Territory</Label>
              <Input value={customerEditForm.territory} onChange={(e) => setCustomerEditForm({ ...customerEditForm, territory: e.target.value })} placeholder="e.g., South Karnataka" />
            </div>
            <div className="p-3 bg-sky-50 rounded-lg border border-sky-200">
              <Label className="text-sky-800 font-semibold mb-2 block">Default Revenue Sharing</Label>
              <p className="text-xs text-sky-600 mb-3">Default shares applied to new packages (can be overridden per package)</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Default Operator Share (%)</Label>
                  <Input type="number" step="0.1" value={customerEditForm.operator_share} onChange={(e) => setCustomerEditForm({ ...customerEditForm, operator_share: e.target.value })} placeholder="e.g., 70" />
                </div>
                <div className="space-y-2">
                  <Label>Default ISP Share (%)</Label>
                  <Input type="number" step="0.1" value={customerEditForm.isp_share} onChange={(e) => setCustomerEditForm({ ...customerEditForm, isp_share: e.target.value })} placeholder="e.g., 30" />
                </div>
              </div>
            </div>
            {customer.customer_type === 'partner' && (
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-amber-800 font-semibold">LCO Management</Label>
                    <p className="text-xs text-amber-600 mt-0.5">Allow this partner to add and manage sub-LCOs (Local Cable Operators)</p>
                  </div>
                  <Checkbox 
                    checked={customerEditForm.lco_management_enabled} 
                    onCheckedChange={(checked) => setCustomerEditForm({ ...customerEditForm, lco_management_enabled: !!checked })}
                    data-testid="lco-management-toggle"
                  />
                </div>
              </div>
            )}
            {(customer.customer_type === 'partner' || customer.customer_type === 'bulk_bandwidth' ||
              customer.customer_type === 'iptv_distributor' || customer.customer_type === 'lco') && (
              <div>
                <Label>ADIGITAL IPTV Token</Label>
                <Input 
                  value={customerEditForm.iptv_access_token} 
                  onChange={e => setCustomerEditForm({ ...customerEditForm, iptv_access_token: e.target.value })}
                  placeholder="x-access-token for ADIGITAL IPTV API"
                  data-testid="iptv-token-input"
                />
                <p className="text-xs text-slate-400 mt-0.5">
                  {customer.customer_type === 'lco'
                    ? 'Optional — if this LCO has their OWN ADIGITAL account, paste the key here. Otherwise leave blank and they inherit from their Distributor / Partner.'
                    : customer.customer_type === 'iptv_distributor'
                      ? 'Optional — this Distributor’s own ADIGITAL key. Overrides their Partner’s key for all their LCOs.'
                      : 'Each partner / bulk-bandwidth customer needs their own ADIGITAL API token'}
                </p>
              </div>
            )}
            <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-orange-800 font-semibold">Railtel Wallet</Label>
                  <p className="text-xs text-orange-700/80 mt-0.5">
                    Show the Railtel quick-action and wallet card on the customer portal dashboard for this account.
                  </p>
                </div>
                <Checkbox
                  checked={customerEditForm.railtel_enabled}
                  onCheckedChange={(checked) => setCustomerEditForm({ ...customerEditForm, railtel_enabled: !!checked })}
                  data-testid="railtel-enabled-toggle"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditCustomerDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateCustomer} className="bg-slate-900 hover:bg-slate-800">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Follow-up Settings Dialog */}
      <Dialog open={followupDialog} onOpenChange={setFollowupDialog}>
        <DialogContent className="sm:max-w-lg" data-testid="payment-followup-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <IndianRupee className="h-5 w-5" /> Payment Follow-up Settings
            </DialogTitle>
            <DialogDescription>
              Enter what the customer owes on a recurring basis and/or as a one-off bill. These
              amounts will surface in <span className="font-medium">Payments → Follow-up</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            <div className="rounded-md border bg-emerald-50/40 border-emerald-200 p-3 space-y-3">
              <p className="text-emerald-800 font-semibold text-sm">Recurring</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Amount (Rs.)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={followupForm.recurring_amount}
                    onChange={e => setFollowupForm({ ...followupForm, recurring_amount: e.target.value })}
                    placeholder="e.g. 1500"
                    data-testid="recurring-amount-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">Cycle (days)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="366"
                    value={followupForm.recurring_cycle_days}
                    onChange={e => setFollowupForm({ ...followupForm, recurring_cycle_days: e.target.value })}
                    placeholder="30"
                    data-testid="recurring-cycle-input"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Next Due Date</Label>
                  <Input
                    type="date"
                    value={followupForm.recurring_next_due_date}
                    onChange={e => setFollowupForm({ ...followupForm, recurring_next_due_date: e.target.value })}
                    data-testid="recurring-due-date-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Auto-rolls forward by the cycle when a payment is recorded.</p>
                </div>
              </div>
            </div>

            <div className="rounded-md border bg-amber-50/40 border-amber-200 p-3 space-y-3">
              <p className="text-amber-800 font-semibold text-sm">Non-Recurring (one-off)</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Amount (Rs.)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={followupForm.non_recurring_amount}
                    onChange={e => setFollowupForm({ ...followupForm, non_recurring_amount: e.target.value })}
                    placeholder="e.g. 5000"
                    data-testid="non-recurring-amount-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">Due Date</Label>
                  <Input
                    type="date"
                    value={followupForm.non_recurring_due_date}
                    onChange={e => setFollowupForm({ ...followupForm, non_recurring_due_date: e.target.value })}
                    data-testid="non-recurring-due-date-input"
                  />
                </div>
              </div>
              <p className="text-xs text-slate-500">Will disappear from follow-up list once fully recorded as paid.</p>
            </div>

            {/* Internal Assignee */}
            <div className="rounded-md border bg-slate-50 p-3 space-y-2">
              <Label className="text-xs font-semibold">Assigned To (internal employee)</Label>
              {staffOptions.length > 0 ? (
                <Select
                  value={followupForm.payment_followup_assignee_email || '__none__'}
                  onValueChange={v => setFollowupForm({
                    ...followupForm,
                    payment_followup_assignee_email: v === '__none__' ? '' : v,
                  })}
                >
                  <SelectTrigger data-testid="followup-assignee-select">
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Not assigned —</SelectItem>
                    {staffOptions.map(s => (
                      <SelectItem key={s.email} value={s.email}>
                        {s.full_name || s.email} ({s.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  type="email"
                  value={followupForm.payment_followup_assignee_email}
                  onChange={e => setFollowupForm({ ...followupForm, payment_followup_assignee_email: e.target.value })}
                  placeholder="e.g. david@assistivenetwork.com"
                  data-testid="followup-assignee-input"
                />
              )}
              <p className="text-xs text-slate-500">
                Reminders can be pinged to this employee via WhatsApp template + email from the
                <span className="font-medium"> Notify</span> button in Payments → Follow-up.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFollowupDialog(false)}>Cancel</Button>
            <Button
              onClick={saveFollowupConfig}
              disabled={followupSaving}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="save-payment-followup-btn"
            >
              {followupSaving ? 'Saving…' : 'Save Settings'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
